import type { ReactElement } from 'react';
import type { StructuredAddress } from './addressFields';

interface AddressFieldsGroupProps {
  idPrefix: string;
  legend: string;
  value: StructuredAddress;
  onChange: (next: StructuredAddress) => void;
}

export function AddressFieldsGroup({ idPrefix, legend, value, onChange }: AddressFieldsGroupProps): ReactElement {
  const set = (key: keyof StructuredAddress, v: string): void => {
    onChange({ ...value, [key]: v });
  };

  return (
    <fieldset className="address-fields">
      <legend>{legend}</legend>
      <div className="address-fields-grid">
        <label className="address-label" htmlFor={`${idPrefix}-line1`}>
          Address line 1
        </label>
        <input
          id={`${idPrefix}-line1`}
          placeholder="Street address, P.O. box"
          value={value.line1}
          onChange={(e) => set('line1', e.target.value)}
          autoComplete="street-address"
        />
        <label className="address-label" htmlFor={`${idPrefix}-line2`}>
          Address line 2 <span className="address-optional">(optional)</span>
        </label>
        <input
          id={`${idPrefix}-line2`}
          placeholder="Apt, suite, unit, building"
          value={value.line2}
          onChange={(e) => set('line2', e.target.value)}
          autoComplete="address-line2"
        />
        <label className="address-label" htmlFor={`${idPrefix}-city`}>
          City
        </label>
        <input
          id={`${idPrefix}-city`}
          placeholder="City"
          value={value.city}
          onChange={(e) => set('city', e.target.value)}
          autoComplete="address-level2"
        />
        <label className="address-label" htmlFor={`${idPrefix}-state`}>
          State / region
        </label>
        <input
          id={`${idPrefix}-state`}
          placeholder="State, province, or region"
          value={value.stateRegion}
          onChange={(e) => set('stateRegion', e.target.value)}
          autoComplete="address-level1"
        />
        <label className="address-label" htmlFor={`${idPrefix}-postal`}>
          Postal code
        </label>
        <input
          id={`${idPrefix}-postal`}
          placeholder="ZIP or postal code"
          value={value.postalCode}
          onChange={(e) => set('postalCode', e.target.value)}
          autoComplete="postal-code"
        />
        <label className="address-label" htmlFor={`${idPrefix}-country`}>
          Country
        </label>
        <input
          id={`${idPrefix}-country`}
          placeholder="Country"
          value={value.country}
          onChange={(e) => set('country', e.target.value)}
          autoComplete="country-name"
        />
      </div>
    </fieldset>
  );
}

export function AddressReadOnly({ value }: { value: string }): ReactElement {
  const lines = value.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!value.trim()) {
    return <span>—</span>;
  }
  if (lines.length === 1) {
    return <span className="multiline">{value}</span>;
  }
  return (
    <ul className="address-readonly">
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  );
}
